import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./customer-list.style.css";
import StyledModal from "../Modal/StyledModalView";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import { isOwnerOrFinance } from "../../utils/role-check";
import InlineContactPersonForm from "./InlineContactPersonForm";

function CustomerListView({
    customers,
    loading,
    error,
    auth,
    onViewCustomer,
    onDeleteCustomer,
    showDeleteModal,
    confirmDeleteCustomer,
    cancelDeleteCustomer,
    showCreateForm,
    setShowCreateForm,
    resetForm,
    newCustomer,
    onNewCustomerChange,
    onCreateCustomer,
    creating,
    vatError,
    handleVatChange,
    kvkError,
    loadingKvk,
    onKvKLookup,
    onAddContactPerson,
    onRemoveContactPerson
}) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const [sortOrder, setSortOrder] = useState("name-asc");

    if (loading) {
        return <Loader message="Bezig met laden..." />;
    }
    if (error) {
        return <NoDataConstruction text="Er is iets fout gegaan!"/>;
    }

    // Filter customers by search
    const filteredCustomers = customers.filter(customer => {
        const searchLower = search.toLowerCase();
        return (
            (customer.name || "").toLowerCase().includes(searchLower) ||
            (customer.email || "").toLowerCase().includes(searchLower) ||
            (customer.city || "").toLowerCase().includes(searchLower) ||
            (customer.contact_person || "").toLowerCase().includes(searchLower) ||
            (customer.kvk_number || "").toLowerCase().includes(searchLower)
        );
    });

    // Sort customers
    const sortedCustomers = [...filteredCustomers].sort((a, b) => {
        switch (sortOrder) {
            case "name-asc":
                return (a.name || "").localeCompare(b.name || "");
            case "name-desc":
                return (b.name || "").localeCompare(a.name || "");
            case "city-asc":
                return (a.city || "").localeCompare(b.city || "");
            case "city-desc":
                return (b.city || "").localeCompare(a.city || "");
            default:
                return 0;
        }
    });

    return (
        <div className="customer-list">
            <div className="list-header">
                <div className="list-header-left">
                    <h2>{t('customers.title')}</h2>
                    <span className="count-badge">{filteredCustomers.length}</span>
                </div>
                {isOwnerOrFinance(auth?.role) && (
                    <button
                        type="button"
                        className="btn btn-add"
                        onClick={() => setShowCreateForm(true)}
                    >
                        {t('customers.addCustomer')}
                    </button>
                )}
            </div>

            {/* Search and Filter Bar - Using Centralized Styling */}
            <section className="filters-bar" aria-label="Customer filters">
                <div className="search-box">
                    <span className="search-icon" aria-hidden="true">🔍</span>
                    <input
                        id="customer-search"
                        className="search-input"
                        type="search"
                        placeholder={t('customers.searchPlaceholder') || "Zoek op naam, email, stad, KvK..."}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        aria-label="Zoek klanten"
                    />
                    {search && (
                        <button
                            type="button"
                            className="clear-btn"
                            onClick={() => setSearch('')}
                            aria-label="Wis zoekopdracht"
                        >
                            ✕
                        </button>
                    )}
                </div>
                <div className="filters-right">
                    <div className="filter-group">
                        <label htmlFor="sort-customers">
                            {t('common.sortBy')}
                        </label>
                        <select
                            id="sort-customers"
                            className="filter-select"
                            value={sortOrder}
                            onChange={e => setSortOrder(e.target.value)}
                            aria-label="Sorteer klanten"
                        >
                            <option value="name-asc">{t('customers.sortNameAsc')}</option>
                            <option value="name-desc">{t('customers.sortNameDesc')}</option>
                            <option value="city-asc">{t('customers.sortCityAsc')}</option>
                            <option value="city-desc">{t('customers.sortCityDesc')}</option>
                        </select>
                    </div>
                </div>
            </section>

            {sortedCustomers.length > 0 && (
                <div className="results-summary" role="status" aria-live="polite">
                    <span className="results-count">
                        {sortedCustomers.length} {sortedCustomers.length === 1 ? 'klant' : 'klanten'}
                    </span>
                    {search && (
                        <span className="results-meta">
                            (gefilterd van {customers.length})
                        </span>
                    )}
                </div>
            )}
            {sortedCustomers.length === 0 ? (
                <NoDataConstruction text={search ? t('customers.noCustomersMatchingSearch') : t('customers.noCustomersFound')}/>
            ) : (
                <>
                    <div className="cards-grid">
                        {sortedCustomers.map((customer) => (
                            <article className="card" key={customer.id}>
                                <div className="card-header">
                                    <div className="card-icon">👤</div>
                                    <div className="card-info">
                                        <h3>{customer.name}</h3>
                                        <div className="card-meta">
                                            {customer.contact_person && (
                                                <span className="card-meta-item">
                                                    📧 {customer.contact_person}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="card-details">
                                    <div className="card-detail-item">
                                        <strong>KVK:</strong>
                                        <span>{customer.kvk_number || t('common.unknown')}</span>
                                    </div>
                                    <div className="card-detail-item">
                                        <strong>BTW:</strong>
                                        <span>{customer.btw_id || t('common.unknown')}</span>
                                    </div>
                                    <div className="card-detail-item">
                                        <strong>{t('common.address')}:</strong>
                                        <span>{customer.address || t('common.unknown')}</span>
                                    </div>
                                    {customer.email && (
                                        <div className="card-detail-item">
                                            <strong>{t('common.email')}:</strong>
                                            <span>{customer.email}</span>
                                        </div>
                                    )}
                                    {customer.phone && (
                                        <div className="card-detail-item">
                                            <strong>{t('common.phone')}:</strong>
                                            <span>{customer.phone}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="card-actions" style={{display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-color)'}}>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => onViewCustomer(customer.id)}
                                    >
                                        {t('customers.viewDetails')}
                                    </button>
                                    {isOwnerOrFinance(auth?.role) && (
                                        <button
                                            type="button"
                                            className="btn btn-delete"
                                            onClick={() => onDeleteCustomer(customer)}
                                        >
                                            {t('common.delete')}
                                        </button>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                </>
            )}

            {/* Create Customer Modal */}
            {showCreateForm && (
                <StyledModal
                    isOpen={showCreateForm}
                    onClose={resetForm}
                    title={t('customers.addCustomer')}
                >
                    <form className="modal-form" onSubmit={onCreateCustomer}>
                        <div className="form-group">
                            <label htmlFor="new-kvk" className="form-label required">
                                {t('customers.kvkNumber')}
                            </label>
                            <div style={{display: 'flex', gap: '0.5rem'}}>
                                <input
                                    id="new-kvk"
                                    type="text"
                                    name="kvk_number"
                                    className="form-input"
                                    value={newCustomer.kvk_number}
                                    onChange={onNewCustomerChange}
                                    placeholder={t('customers.kvkNumber')}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onKvKLookup}
                                    disabled={loadingKvk || !newCustomer.kvk_number}
                                    style={{whiteSpace: 'nowrap'}}
                                >
                                    {loadingKvk ? t('customers.fetchingKvk') : t('customers.lookupKvk')}
                                </button>
                            </div>
                            {kvkError && <span className="form-error" role="alert">{kvkError}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="new-name" className="form-label required">
                                {t('customers.customerName')}
                            </label>
                            <input
                                id="new-name"
                                type="text"
                                name="name"
                                className="form-input"
                                value={newCustomer.name}
                                onChange={onNewCustomerChange}
                                placeholder={t('customers.customerName')}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="new-address" className="form-label required">
                                {t('common.address')}
                            </label>
                            <input
                                id="new-address"
                                type="text"
                                name="address"
                                className="form-input"
                                value={newCustomer.address}
                                onChange={onNewCustomerChange}
                                placeholder={t('common.address')}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="new-btw" className="form-label required">
                                {t('customers.btwId')}
                            </label>
                            <input
                                id="new-btw"
                                type="text"
                                name="btw_id"
                                className="form-input"
                                value={newCustomer.btw_id}
                                onChange={handleVatChange}
                                placeholder={t('customers.btwId')}
                                required
                            />
                            {vatError && <span className="form-error" role="alert">{vatError}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="new-email" className="form-label">
                                {t('common.email')}
                            </label>
                            <input
                                id="new-email"
                                type="email"
                                name="email"
                                className="form-input"
                                value={newCustomer.email}
                                onChange={onNewCustomerChange}
                                placeholder={t('common.email')}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="new-phone" className="form-label">
                                {t('common.phone')}
                            </label>
                            <input
                                id="new-phone"
                                type="tel"
                                name="phone"
                                className="form-input"
                                value={newCustomer.phone || ""}
                                onChange={onNewCustomerChange}
                                placeholder={t('common.phone')}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="new-contact" className="form-label">
                                {t('customers.mainContactPersonLegacy')}
                            </label>
                            <input
                                id="new-contact"
                                type="text"
                                name="contact_person"
                                className="form-input"
                                value={newCustomer.contact_person || ""}
                                onChange={onNewCustomerChange}
                                placeholder={t('customers.mainContactPersonLegacy')}
                            />
                        </div>

                        <div className="form-group">
                            <InlineContactPersonForm
                                contactPersons={newCustomer.contact_persons || []}
                                onAdd={onAddContactPerson}
                                onRemove={onRemoveContactPerson}
                            />
                        </div>

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn btn-cancel"
                                onClick={resetForm}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                className="btn btn-save"
                                disabled={creating}
                            >
                                {creating ? t('customers.adding') : t('common.add')}
                            </button>
                        </div>
                    </form>
                </StyledModal>
            )}

            {/* Delete Customer Modal */}
            <StyledModal
                isOpen={showDeleteModal}
                onClose={cancelDeleteCustomer}
                title={t('customers.confirmDeleteCustomer')}
                confirmText={t('common.yes') + ', ' + t('common.delete').toLowerCase()}
                closeText={t('common.cancel')}
                onConfirm={confirmDeleteCustomer}
            >
                <p>{t('customers.confirmDeleteCustomerMessage')}</p>
            </StyledModal>
        </div>
    );
}

CustomerListView.propTypes = {
    customers: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    auth: PropTypes.object,
    onViewCustomer: PropTypes.func.isRequired,
    onDeleteCustomer: PropTypes.func.isRequired,
    showDeleteModal: PropTypes.bool.isRequired,
    confirmDeleteCustomer: PropTypes.func.isRequired,
    cancelDeleteCustomer: PropTypes.func.isRequired,
    showCreateForm: PropTypes.bool.isRequired,
    setShowCreateForm: PropTypes.func.isRequired,
    resetForm: PropTypes.func.isRequired,
    newCustomer: PropTypes.object.isRequired,
    onNewCustomerChange: PropTypes.func.isRequired,
    onCreateCustomer: PropTypes.func.isRequired,
    creating: PropTypes.bool,
    vatError: PropTypes.string,
    handleVatChange: PropTypes.func.isRequired,
    kvkError: PropTypes.string,
    loadingKvk: PropTypes.bool,
    onKvKLookup: PropTypes.func.isRequired,
    onAddContactPerson: PropTypes.func.isRequired,
    onRemoveContactPerson: PropTypes.func.isRequired,
};

export default CustomerListView;
